import { useMemo, useState } from "react";
import useMeta from "@/hooks/useMeta";
import { news } from "@/mocks/news";
import type { WorkType } from "@/types/media";
import { TYPE_LABELS } from "@/types/media";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import Breadcrumb from "@/components/base/Breadcrumb";

const PAGE_SIZE = 5;

type Filter = "all" | WorkType;

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影资讯" },
  { key: "tv", label: "电视剧资讯" },
  { key: "variety", label: "综艺资讯" },
];

export default function NewsListPage() {
  const [filter, setFilter] = useState<Filter>("all");
  const [page, setPage] = useState(1);

  useMeta({
    title: "影视资讯 - 大象传媒",
    description:
      "大象传媒影视资讯频道，汇集电影、电视剧与综艺领域的最新动态、行业观察与深度报道，助你把握影视内容风向。",
    keywords: "影视资讯,电影资讯,电视剧资讯,综艺资讯,大象传媒",
  });

  const sorted = useMemo(() => [...news].sort((a, b) => b.date.localeCompare(a.date)), []);

  const filtered = useMemo(
    () => (filter === "all" ? sorted : sorted.filter((n) => n.category === filter)),
    [sorted, filter]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "影视资讯" }]} />

      <div className="mt-4 mb-6">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          影视资讯
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          追踪影视行业最新动态，解读作品背后的故事，为你带来及时、专业的内容资讯。
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => {
              setFilter(f.key);
              setPage(1);
            }}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              filter === f.key
                ? "bg-primary-600 text-background-50"
                : "border border-background-300 bg-background-100 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
            }`}
          >
            {f.label}
          </button>
        ))}
        <span className="ml-auto text-sm text-foreground-500">
          共 {filtered.length} 篇资讯
        </span>
      </div>

      {paged.length === 0 ? (
        <EmptyState
          icon="ri-newspaper-line"
          title="暂无相关资讯"
          description="该分类下暂时还没有资讯内容，试试切换其他分类看看。"
          actionLabel="查看全部资讯"
          onAction={() => {
            setFilter("all");
            setPage(1);
          }}
        />
      ) : (
        <>
          <div className="flex flex-col gap-4">
            {paged.map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
          <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
        </>
      )}

      <section className="mt-10 rounded-lg border border-background-200 bg-primary-50 p-5 md:p-6">
        <h2 className="font-heading text-base font-bold text-foreground-950">
          资讯分类说明
        </h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {(["movie", "tv", "variety"] as WorkType[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setFilter(t);
                setPage(1);
              }}
              className="flex flex-col items-start gap-1 rounded-md border border-background-200 bg-background-50 p-4 text-left transition-colors hover:border-primary-300"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i
                  className={`${
                    t === "movie"
                      ? "ri-film-line"
                      : t === "tv"
                      ? "ri-tv-2-line"
                      : "ri-mic-line"
                  } text-lg`}
                ></i>
              </span>
              <span className="font-medium text-foreground-950">
                {TYPE_LABELS[t]}资讯
              </span>
              <span className="text-xs text-foreground-500">
                查看{TYPE_LABELS[t]}领域的最新动态
              </span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}